<template>
  <div>
    <v-btn
      v-if="dnsMismatch"
      elevation="0"
      outlined
      x-small
      color="error"
      class="mr-4"
      :loading="loading"
      @click="loadModal"
    >
      <v-icon size="24">$alertwarning</v-icon>
      <span
        class="text--heading p-4 font-weight-bold"
        style="white-space: nowrap"
        >{{ $t("message.dnsPropagationInProgress") }}</span
      >
    </v-btn>
    <v-btn
      v-else-if="
        !dnsMismatch && instance.hasChangeDomainPrivilege()
      "
      elevation="0"
      outlined
      x-small
      color="primary"
      class="mr-4"
      :loading="loading"
      @click="loadModal"
    >
      <span
        class="text--heading p-4 font-weight-bold"
        style="white-space: nowrap"
        >{{ $t("button.setDomain.changeDomain") }}</span
      >
    </v-btn>
    <v-dialog
      ref="dialog"
      v-model="isOpen"
      transition="custom-dialog-transition"
    >
      <div class="card-overlay" @click="isOpen = !isOpen" />
      <v-card style="width: 560px">
        <v-card-title class="pb-6 flex justify-space-between align-center">
          <h4 class="font-weight-light">
            {{ $t("heading.instance.modal.setDomain.title") }}
          </h4>
          <v-btn icon x-small @click="isOpen = false">
            <v-icon size="24">$close</v-icon>
          </v-btn>
        </v-card-title>
        <template v-if="step === 0">
          <v-card-text>
            <v-form ref="form" @submit.prevent="nextStep">
              <v-expansion-panels v-model="panelOpen">
                <v-expansion-panel elevation="0">
                  <v-expansion-panel-header
                    class="h6 text--heading font-weight-bold"
                  >
                    {{
                      $t("heading.instance.modal.setDomain.set.domain.title")
                    }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    {{ $t("form.label.domainName") }}
                    <a
                      class="float-right info--text"
                      v-if="orderDomainLink"
                      target="_blank"
                      :href="orderDomainLink"
                    >
                      {{ $t("button.domain.order") }}
                    </a>
                    <v-text-field
                      v-if="!panelOpen"
                      class="mt-2"
                      outlined
                      placeholder="mydomain.com"
                      v-model="clientDomainName"
                      hide-details=""
                      validate-on-blur
                      :rules="[
                        (v) =>
                          !!v ||
                          $t('validation.required', {
                            field: $t('form.label.domainName'),
                          }),
                        (v) =>
                          isValidDomain(v) ||
                          $t('form.validation.valid', {
                            field: $t('form.label.domainName'),
                          }),
                      ]"
                    />
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel v-if="freeSubdomains.length > 0">
                  <v-expansion-panel-header
                    class="h6 text--heading font-weight-bold"
                  >
                    {{
                      $t("heading.instance.modal.setDomain.set.subdomain.title")
                    }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div
                      v-if="panelOpen === 1"
                      class="flex flex-row align-center"
                      style="display: flex"
                    >
                      <v-text-field
                        class=""
                        outlined
                        placeholder="mydomain"
                        v-model="freeDomainName"
                        hide-details=""
                        validate-on-blur
                        max-width="250px"
                        :rules="[
                          (v) =>
                            !!v ||
                            $t('validation.required', {
                              field: $t('general.subdomain'),
                            }),
                        ]"
                      />
                      <span class="mx-2 mb-6">.</span>
                      <v-select
                        outlined
                        :items="freeSubdomains"
                        v-model="freeDomainAffix"
                        hide-details=""
                        validate-on-blur
                        max-width="250px"
                        :rules="[
                          (v) =>
                            !!v ||
                            $t('validation.required', {
                              field: $t('form.label.domain'),
                            }),
                        ]"
                        :menu-props="{
                          offsetY: true,
                          nudgeBottom: '8px',
                          closeOnContentClick: false,
                          contentClass:
                            'custom-dropdown-select custom-dropdown-select--onboarding',
                        }"
                      />
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-form>
          </v-card-text>

          <v-card-actions class="d-flex flex-column pb-4 px-8 pt-6">
            <v-btn
              x-large
              elevation="0"
              color="primary"
              style="color: var(--v-white-base)"
              block
              :loading="submitting"
              @click="nextStep"
            >
              {{ $t("button.setDomain.useThisDomain") }}
            </v-btn>
            <v-btn
              x-large
              elevation="0"
              class="mx-0 mt-2"
              color="gray"
              text
              block
              @click="isOpen = false"
            >
              <span class="p-1 font-weight-light gray--text text--darken-1">
                {{ $t("button.cancel") }}
              </span>
            </v-btn>
          </v-card-actions>
        </template>
        <template v-else-if="step === 1">
          <v-card-text class="pb-8">
            <div class="domain-container">
              <span class="p-3">
                {{ $t("heading.instance.modal.setDomain.config.subtitle") }}
                <span class="text--heading font-weight-600">
                  {{ instance.domain }}
                </span>
              </span>

              <v-btn
                color="primary"
                x-small
                outlined
                elevation="0"
                @click="step = 0"
                v-if="instance.hasChangeDomainPrivilege()"
              >
                {{ $t("button.change") }}
              </v-btn>
            </div>
            <div v-if="isAppSingleServer" class="domain-container">
              <span class="p-3">
                {{ $t("heading.instance.modal.setDomain.previewSite") }}
              </span>
              <v-btn
                color="primary"
                x-small
                elevation="0"
                @click="
                  isOpen = false;
                  previewSiteModelOpen = true;
                "
              >
                {{ $t("heading.instance.modal.setDomain.generateUrl") }}
              </v-btn>
              <PreviewSiteModal
                :is-open.sync="previewSiteModelOpen"
                :instance="instance"
                @close="previewSiteModelOpen = false"
              />
            </div>
            <DnsOptions
              :domain="instance.domain"
              :onboardingSubdomain="instance.onboarding_subdomain"
              :dnsSettings="instance.dns_settings"
              :ipv4Addresses="ipv4Addresses"
              :hasDnsServer="instance.service_has_dns"
              :nameservers="instance.dns_nameservers"
              :host-c-names="instance.host_c_names ?? []"
              :dnsServerRecords="
                instance.domain_zone_details?.configuration_records ?? []
              "
            />
          </v-card-text>
          <v-card-actions class="d-flex flex-column pb-4 px-8 pt-0">
            <v-btn
              x-large
              elevation="0"
              color="primary"
              style="color: var(--v-white-base)"
              block
              @click="isOpen = false"
            >
              {{ $t("button.close") }}
            </v-btn>
          </v-card-actions>
        </template>

        <template v-else-if="step === 2">
          <v-card-text>
            <div v-if="localErrorMessage" class="mb-6 alert-box alert">
              <span class="font-weight-600 mb-0">{{ localErrorMessage }}</span>
            </div>
            <template v-if="instance.plan_settings.domain_transfer_enabled">
              <h6 class="p-2 font-weight-light">
                <i18next
                  :translation="
                    $t('heading.onboarding.transferDomain.subtitle')
                  "
                >
                  <template #domain
                    ><span class="font-weight-600">{{
                      domainComputed
                    }}</span></template
                  >
                </i18next>
              </h6>
              <div class="p-2 mt-2 text-justify text--darken1">
                <p class="mb-4 text--darken1">
                  <i18next
                    :translation="
                      $t('heading.onboarding.transferDomain.message.possible.title')
                    "
                  >
                    <template #domain
                      ><span class="font-weight-800">{{
                        domainComputed
                      }}</span></template
                    >
                  </i18next>
                </p>

                <p class="mb-2 text--darken1">
                  {{
                    $t(
                      "heading.onboarding.transferDomain.message.possible.stepsTitle"
                    )
                  }}
                </p>

                <ol class="pl-4">
                  <li class="mb-1 text--darken1">
                    <i18next
                      :translation="
                        $t('heading.onboarding.transferDomain.message.possible.step1')
                      "
                    >
                      <template #domain
                        ><span class="font-weight-800">{{
                          domainComputed
                        }}</span></template
                      >
                    </i18next>
                  </li>
                  <li class="mb-1 text--darken1">
                    <i18next
                      :translation="
                        $t('heading.onboarding.transferDomain.message.possible.step2WithHost')
                      "
                    >
                      <template #domain><span class="font-weight-800">{{ domainVerificationDetails.name }}</span></template>
                      <template #recordHost><span class="font-weight-800">@</span></template>
                      <template #recordType
                        ><span class="font-weight-800">{{
                          domainVerificationDetails.type
                        }}</span></template
                      >
                    </i18next>
                  </li>
                  <li class="mb-1 text--darken1">
                    {{
                      $t(
                        "heading.onboarding.transferDomain.message.possible.step3"
                      )
                    }}
                  </li>
                  <li class="text--darken1">
                    {{
                      $t(
                        "heading.onboarding.transferDomain.message.possible.step4"
                      )
                    }}
                  </li>
                </ol>
              </div>
              <long-value-box
                class="mt-4"
                v-if="domainVerificationDetails && domainVerificationDetails.content"
                :value="domainVerificationDetails.content"
                :can-expand="false"
              />
            </template>
            <template v-else>
              <h6 class="p-2 font-weight-light">
                <i18next
                  :translation="
                    $t('heading.onboarding.transferDomain.subtitle')
                  "
                >
                  <template #domain
                    ><span class="font-weight-600">{{
                      domainComputed
                    }}</span></template
                  >
                </i18next>
              </h6>
              <p class="p-3 mt-2">
                {{ $t("heading.onboarding.transferDomain.message.impossible") }}
              </p>
            </template>
          </v-card-text>
          <v-card-actions class="d-flex flex-column pb-4 px-8 pt-6">
            <template v-if="instance.plan_settings.domain_transfer_enabled">
              <v-btn
                x-large
                elevation="0"
                color="primary"
                block
                :loading="submitting"
                @click="nextStep"
              >
                {{ $t("button.transferNow") }}
              </v-btn>
              <v-btn
                x-large
                elevation="0"
                class="mx-0 mt-2"
                color="gray"
                text
                block
                @click="isOpen = false"
              >
                <span class="p-1 font-weight-light gray--text text--darken-1">
                  {{ $t("button.cancel") }}
                </span>
              </v-btn>
            </template>
            <template v-else>
              <v-btn
                x-large
                elevation="0"
                color="primary"
                block
                @click="isOpen = false"
                >{{ $t("button.cancel") }}</v-btn
              >
            </template>
          </v-card-actions>
        </template>

        <!-- DOMAIN DNS VERIFICATION -->
        <template v-if="step === 3">
          <v-card-text height="800px" class="px-10 pt-6">
            <div
              class="mb-6 alert-box"
              :class="isVerificationSuccessful ? 'ok' : 'alert'"
            >
              <span class="font-weight-600 mb-0">
                <span v-if="isVerificationSuccessful">
                  <i18next
                    :translation="
                      $t(
                        'heading.onboarding.domainDnsVerificationModal.message.success'
                      )
                    "
                  >
                    <template #domain
                      ><span class="font-weight-600">{{
                        domain
                      }}</span></template
                    >
                  </i18next>
                </span>
                <span v-else-if="!isRetried">
                  <i18next
                    :translation="
                      $t(
                        'heading.onboarding.domainDnsVerificationModal.message.incorrect'
                      )
                    "
                  >
                    <template #domain
                      ><span class="font-weight-600">{{
                        domain
                      }}</span></template
                    >
                  </i18next>
                </span>
                <span v-else>{{
                  $t(
                    "heading.onboarding.domainDnsVerificationModal.message.tryAgain"
                  )
                }}</span>
              </span>
            </div>
            <DnsOptions
              :domain="clientDomainName"
              :onboardingSubdomain="null"
              :dnsSettings="instance.dns_settings"
              :ipv4Addresses="ipv4Addresses"
              :hasDnsServer="instance.service_has_dns"
              :nameservers="instance.dns_nameservers"
              :host-c-names="instance.host_c_names ?? []"
              hideTitle
            />
          </v-card-text>

          <v-card-actions class="d-flex flex-column pb-6 px-10 pt-2 mt-4">
            <v-btn
              v-if="!isVerificationSuccessful"
              x-large
              elevation="0"
              color="primary"
              block
              @click="verifyDomainDnsPropagation()"
              :loading="submitting"
              >{{ $t("button.verifyDomain") }}
            </v-btn>
            <v-btn
              v-else
              x-large
              elevation="0"
              color="primary"
              block
              @click="nextStep"
              :loading="submitting"
            >
              {{ $t("button.setDomain.changeDomain") }}
            </v-btn>
            <v-btn
              v-if="!isVerificationSuccessful"
              @click="changeDomain"
              x-large
              elevation="0"
              class="mx-0 mt-2"
              color="gray"
              text
              block
            >
              <span class="p-1 font-weight-light gray--text text--darken-1">
                {{ $t("button.skip") }}
              </span>
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Api from "@/apis/Api";
import LongValueBox from "@/components/common/LongValueBox.vue";
import DnsOptions from "../DnsOptions.vue";
import DomainValidationMixin from "../../mixins/DomainValidationMixin";
import PreviewSiteModal from "./PreviewSiteModal.vue";
export default {
  components: { LongValueBox, DnsOptions, PreviewSiteModal },
  mixins: [DomainValidationMixin],
  data: function () {
    return {
      isOpen: false,
      infoTab: 0,
      panelOpen: 0,
      dnsMismatch: false,
      step: 0,
      loading: false,
      localErrorMessage: "",
      clientDomainName: "",
      freeDomainAffix: "",
      freeDomainName: "",
      freeSubdomains: [],
      submitting: false,
      ipAddress: "",
      ipv4Addresses: [],
      nameservers: [],
      //orderDomainLink: "",
      domainVerificationDetails: {},
      isRetried: false,
      isVerificationSuccessful: false,
      previewSiteModelOpen: false,
    };
  },
  props: {
    instance: Object,
  },
  computed: {
    isAppSingleServer() {
      return !!this.$store.state.home.app.single_server?.active;
    },
    orderDomainLink() {
      return this.$store.state.home.user.order_domain_link;
    },
    domainComputed() {
      return this.panelOpen === 0
        ? this.clientDomainName
        : `${this.freeDomainName}.${this.freeDomainAffix}`;
    },
  },
  methods: {
    loadModal() {
      this.loading = true;
      this.localErrorMessage = "";
      return Api.cached()
        .get(`/instances/${this.instance.id}/change-domain/options`)
        .then((response) => {
          this.freeSubdomains = response.data.data.subdomains;
          this.freeDomainAffix = this.freeSubdomains[0];
          this.ipAddress = response.data.data.ip_address;
          this.ipv4Addresses = response.data.data.ipv4_addresses;
          this.nameservers = response.data.data.nameservers;
          //this.orderDomainLink = response.data.data.order_domain_link;
          this.isOpen = true;
        })
        .catch((error) => {
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async checkDnsPropagation() {
      this.loading = true;
      let result = await Api.dns().verifyDomainResolving(
        this.instance.domain,
        this.instance.onboarding_subdomain,
        this.instance.dns_settings,
        this.instance.service_has_dns,
        this.instance.host_ipv4_addresses,
        this.instance.dns_nameservers,
        this.instance.host_c_names ?? [],
        this.instance.domain_zone_details?.configuration_records ?? []
      );
      this.instance.setDnsResolveStatus(result);
      this.loading = false;
      this.dnsMismatch = !result;
      if (this.dnsMismatch) {
        this.$emit("dns-propagation-in-progress");
      }
      this.step = 1;
    },
    async nextStep() {
      this.localErrorMessage = "";
      if (this.step === 0) {
        if (!this.$refs.form.validate()) {
          return;
        }

        if (this.instance.server_settings.domain_transfer_allowed) {
          let result = await this.verifyDomain();
          if (result.verification_required) {
            this.domainVerificationDetails = result.verification_record;
            this.step = 2;
            return;
          }
        }
      }

      if (this.step === 2 && this.instance.server_settings.domain_transfer_allowed) {
        let result = await this.verifyDomain();
        if (!result) {
          // error message already set
          return;
        }
        if (result.verification_required) {
          this.domainVerificationDetails = result.verification_record;
          this.localErrorMessage = this.$t(
            "heading.onboarding.transferDomain.message.verificationRequired"
          );
          return;
        }
      }

      if (
        (this.step === 0 || this.step === 2) &&
        this.panelOpen === 0 && // only for change domain
        this.instance.plan_settings.eager_domain_dns_verification_enabled
      ) {
        await this.verifyDomainDnsPropagation(true);
        if (!this.isNameserversCorrected) {
          this.step = 3;
          return;
        }
      }

      this.changeDomain();
    },
    verifyDomain() {
      this.submitting = true;
      return Api.get(
        `/services/${this.instance.service_id}/domains/verify?domain=${this.domainComputed}`
      )
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          this.localErrorMessage = Api.getErrorMessage(error);
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    async verifyDomainDnsPropagation(firstAttempt = false) {
      this.submitting = true;
      try {
        if (!firstAttempt) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }

        let result = await Api.dns().verifyDomainResolving(
          this.domainComputed,
          this.instance.onboarding_subdomain,
          this.instance.dns_settings,
          this.instance.service_has_dns,
          this.instance.host_ipv4_addresses,
          this.instance.service_nameservers,
          this.instance.host_c_names ?? [],
          this.instance.domain_zone_details?.configuration_records ?? [],
          this.instance.plan_settings.eager_domain_dns_verification_enabled
        );
        this.instance.setDnsResolveStatus(result);
        if (result) {
          this.isNameserversCorrected = true;
        } else if (!firstAttempt) {
          this.isRetried = true;
        }
      } finally {
        this.submitting = false;
      }
    },
    changeDomain() {
      if (this.panelOpen === 0) {
        this.submitting = true;
        Api.put(`/instances/${this.instance.id}/change-domain/domain`, {
          domain: this.clientDomainName,
        })
          .then((response) => {
            this.checkDnsPropagation().then(() => {
              this.$emit("instance-published", response.data.data);
              this.nameservers = response.data.data.dns_nameservers;
              this.step = 1;
            });
          })
          .catch((error) => {
            this.$store.dispatch("addAlert", {
              success: false,
              errorMessage: Api.getErrorMessage(error),
            });
          })
          .finally(() => {
            this.submitting = false;
          });
      } else {
        this.submitting = true;
        Api.put(`/instances/${this.instance.id}/change-domain/subdomain`, {
          subdomain: this.freeDomainName,
          domain: this.freeDomainAffix,
        })
          .then((response) => {
            this.checkDnsPropagation().then(() => {
              this.$emit("instance-published", response.data.data);
              this.isOpen = false;
            });
          })
          .catch((error) => {
            this.$store.dispatch("addAlert", {
              success: false,
              errorMessage: Api.getErrorMessage(error),
            });
          })
          .finally(() => {
            this.submitting = false;
          });
      }
    },
  },
  watch: {
    isOpen: function (value) {
      value
        ? this.$store.dispatch("lockBodyScroll")
        : this.$store.dispatch("unlockBodyScroll");
    },
  },
  mounted() {
    if (this.instance.hasIpAddressInUrl()) {
      return;
    }
    if (this.instance.getDnsAlertsEnabled()) {
      this.checkDnsPropagation();
    }
  },
};
</script>

<style lang="scss" scoped>
.v-card__text {
  -webkit-line-clamp: unset;
}

.v-expansion-panel {
  border: 1px solid var(--v-gray-lighten2);

  &::before {
    display: none;
  }

  &::after {
    display: none;
  }

  &--active {
    border-color: var(--v-primary-base);
  }

  .v-expansion-panel-content::v-deep {
    .v-expansion-panel-content__wrap {
      padding-bottom: 0px;
      color: var(--v-gray-lighten1);
    }
  }

  .v-expansion-panel-header {
    padding-left: 60px;
    position: relative;

    &::before,
    &::after {
      content: "";
      position: absolute;
      left: 24px;
      top: 50%;
      transform: translateY(-50%);
      opacity: 1 !important;
      background: var(--v-checkbox-base);
      border-radius: 20px;
    }

    &::before {
      transition: border-color 0.24s ease, background-color 0.24s ease;
      width: 20px;
      height: 20px;
      border: 1px solid var(--v-gray-lighten2);
    }

    &::after {
      width: 10px;
      height: 10px;
      z-index: 10;
      left: 29px;
    }

    &:hover {
      &::before {
        border-color: var(--v-primary-base);
      }
    }

    &--active {
      &:before {
        border-color: var(--v-primary-base);
        background-color: var(--v-primary-base);
      }
    }
  }
}

.domain-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 6px;
  border: 1px solid var(--v-gray-lighten2);
  padding: 16px 16px;
  margin-bottom: 16px;
}

.domain-info {
  border-radius: 6px;
  border: 1px solid var(--v-gray-lighten2);
  padding: 6px 16px 16px 16px;
  background: var(--v-panel-base);
  margin-bottom: 24px;
}
.v-expansion-panel {
  background-color: unset !important;
}

.error--text {
  border: 1px solid var(--v-error-base) !important;
  .v-btn__content {
    .v-icon {
      color: var(--v-error-base) !important;
    }
  }
}

.error--text:hover {
  background: var(--v-error-lighten5) !important;
  border: 1px solid var(--v-error-base) !important;
}

.main-warning-message {
  background: var(--v-primary-lighten4);
  border-radius: 6px;

  h5 {
    margin: 0;
    font-size: 1.1em;
    color: var(--v-primary-base);

    strong {
      color: var(--v-primary-darken1) !important;
    }
  }
}

.alert-box {
  width: 100%;
  min-width: 100%;
  padding: 16px;
  text-align: center;
  border-radius: 8px;

  &.ok {
    background: var(--v-success-lighten5);
    color: var(--v-success-base);
  }

  &.alert {
    background: var(--v-error-lighten5);
    color: var(--v-error-base);
  }
}
</style>